/**
 * The deterministic tick engine. All game logic lives here.
 *
 * Contract: createGame(config) -> GameState; applyTick(state, commands)
 * advances exactly one tick in place. Same config + same command stream =
 * same game, everywhere (browser solo play, every multiplayer peer).
 */

import { Rng } from './rng';
import {
  BUILDINGS,
  CYBER,
  ECONOMY,
  FACTIONS,
  FRONTIER,
  marketSaturationMult,
  PHASE_ADVANCE,
  TECHS,
  UNITS,
} from './data';
import type {
  Building,
  BuildingType,
  FactionId,
  Command,
  DataNode,
  Entity,
  EntityId,
  GameConfig,
  GameEvent,
  GameMap,
  GameState,
  IssuedCommand,
  Market,
  Phase,
  PlayerIdx,
  PlayerState,
  TechId,
  TimelineEventKind,
  Unit,
  UnitType,
  Vec2,
} from './types';
import { PHASE_NAMES, TICKS_PER_SECOND } from './types';

const TPS = TICKS_PER_SECOND;
const secToTicks = (s: number) => Math.max(1, Math.round(s * TPS));

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

export function dist(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function tileIdx(map: GameMap, x: number, y: number): number {
  return y * map.width + x;
}

function inBounds(map: GameMap, x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < map.width && y < map.height;
}

/** Single source of truth for placement legality: in-bounds with a 1-tile
 * border margin (nothing hugs or clips the map edge), terrain/building
 * footprints clear, and no units/nodes/markets on the tiles. Used by the
 * build command, the client's placement ghost, and the copilot. */
export function canPlaceBuilding(
  state: GameState,
  type: BuildingType,
  x: number,
  y: number,
): boolean {
  const stats = BUILDINGS[type];
  const tx = Math.floor(x);
  const ty = Math.floor(y);
  if (
    tx < 1 ||
    ty < 1 ||
    tx + stats.size > state.map.width - 1 ||
    ty + stats.size > state.map.height - 1
  ) {
    return false;
  }
  for (let dy = 0; dy < stats.size; dy++) {
    for (let dx = 0; dx < stats.size; dx++) {
      if (isBlocked(state, tx + dx, ty + dy)) return false;
      for (const e of state.entities) {
        if (e.kind === 'building') continue;
        if (Math.floor(e.pos.x) === tx + dx && Math.floor(e.pos.y) === ty + dy) {
          return false;
        }
      }
    }
  }
  return true;
}

/** Faction-aware building rules: serverless has no GPU clusters at all
 * (and correspondingly no cluster prerequisites). */
export function canFactionBuild(faction: FactionId, type: BuildingType): boolean {
  if (FACTIONS[faction].tokenless && type === 'gpuCluster') return false;
  return true;
}

export function buildingRequirementMet(
  state: GameState,
  player: PlayerIdx,
  type: BuildingType,
): boolean {
  const req = BUILDINGS[type].requires;
  if (!req) return true;
  const p = state.players[player];
  if (FACTIONS[p.faction].tokenless && req === 'gpuCluster') return true; // waived
  return playerBuildings(state, player).some((b) => b.type === req && b.buildTicks === 0);
}

/** Units that can serve market demand (inference agents + faction variants). */
export function isServeUnit(t: UnitType): boolean {
  return Boolean(UNITS[t].serves);
}

export function isBlocked(state: GameState, x: number, y: number): boolean {
  const tx = Math.floor(x);
  const ty = Math.floor(y);
  if (!inBounds(state.map, tx, ty)) return true;
  if (state.map.terrain[tileIdx(state.map, tx, ty)] === 1) return true;
  // building footprints block
  for (const e of state.entities) {
    if (e.kind === 'building') {
      const s = BUILDINGS[e.type].size;
      if (tx >= e.pos.x && tx < e.pos.x + s && ty >= e.pos.y && ty < e.pos.y + s) {
        return true;
      }
    }
  }
  return false;
}

/** Cheap A* on the tile grid. Returns waypoints (tile centers), or a direct
 * step list toward the goal when no path exists (units nibble at edges). */
function findPath(state: GameState, from: Vec2, to: Vec2): Vec2[] {
  const map = state.map;
  const sx = Math.floor(from.x);
  const sy = Math.floor(from.y);
  let gx = Math.floor(to.x);
  let gy = Math.floor(to.y);
  gx = Math.max(0, Math.min(map.width - 1, gx));
  gy = Math.max(0, Math.min(map.height - 1, gy));

  // Precompute blocked grid once per query (buildings included)
  const blocked = new Uint8Array(map.width * map.height);
  for (let i = 0; i < map.terrain.length; i++) blocked[i] = map.terrain[i];
  for (const e of state.entities) {
    if (e.kind === 'building') {
      const s = BUILDINGS[e.type].size;
      for (let by = e.pos.y; by < e.pos.y + s; by++) {
        for (let bx = e.pos.x; bx < e.pos.x + s; bx++) {
          if (inBounds(map, bx, by)) blocked[tileIdx(map, bx, by)] = 1;
        }
      }
    }
  }
  // If the goal tile itself is blocked, walk to the nearest free neighbor
  if (blocked[tileIdx(map, gx, gy)]) {
    let best: Vec2 | null = null;
    let bestD = Infinity;
    for (let r = 1; r <= 4 && !best; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          const nx = gx + dx;
          const ny = gy + dy;
          if (!inBounds(map, nx, ny) || blocked[tileIdx(map, nx, ny)]) continue;
          const d = Math.abs(dx) + Math.abs(dy);
          if (d < bestD) {
            bestD = d;
            best = { x: nx, y: ny };
          }
        }
      }
    }
    if (!best) return [];
    gx = best.x;
    gy = best.y;
  }

  const W = map.width;
  const open: number[] = [tileIdx(map, sx, sy)];
  const came = new Int32Array(W * map.height).fill(-1);
  const g = new Float32Array(W * map.height).fill(Infinity);
  const start = tileIdx(map, sx, sy);
  const goal = tileIdx(map, gx, gy);
  g[start] = 0;
  const h = (i: number) => {
    const x = i % W;
    const y = Math.floor(i / W);
    return Math.abs(x - gx) + Math.abs(y - gy);
  };
  const inOpen = new Uint8Array(W * map.height);
  inOpen[start] = 1;
  let iterations = 0;

  while (open.length > 0 && iterations++ < 4000) {
    // linear-scan min (grid is small; keeps it simple & deterministic)
    let bi = 0;
    let bf = g[open[0]] + h(open[0]);
    for (let i = 1; i < open.length; i++) {
      const f = g[open[i]] + h(open[i]);
      if (f < bf) {
        bf = f;
        bi = i;
      }
    }
    const cur = open.splice(bi, 1)[0];
    inOpen[cur] = 0;
    if (cur === goal) {
      // reconstruct
      const path: Vec2[] = [];
      let c = cur;
      while (c !== start && c !== -1) {
        path.push({ x: (c % W) + 0.5, y: Math.floor(c / W) + 0.5 });
        c = came[c];
      }
      path.reverse();
      return path;
    }
    const cx = cur % W;
    const cy = Math.floor(cur / W);
    const neighbors = [
      [cx + 1, cy, 1],
      [cx - 1, cy, 1],
      [cx, cy + 1, 1],
      [cx, cy - 1, 1],
      [cx + 1, cy + 1, 1.414],
      [cx - 1, cy + 1, 1.414],
      [cx + 1, cy - 1, 1.414],
      [cx - 1, cy - 1, 1.414],
    ] as const;
    for (const [nx, ny, cost] of neighbors) {
      if (!inBounds(map, nx, ny)) continue;
      const ni = tileIdx(map, nx, ny);
      if (blocked[ni]) continue;
      const ng = g[cur] + cost;
      if (ng < g[ni]) {
        g[ni] = ng;
        came[ni] = cur;
        if (!inOpen[ni]) {
          open.push(ni);
          inOpen[ni] = 1;
        }
      }
    }
  }
  return []; // unreachable
}

// ---------------------------------------------------------------------------
// Entity helpers
// ---------------------------------------------------------------------------

export function getEntity(state: GameState, id: EntityId): Entity | undefined {
  return state.entities.find((e) => e.id === id);
}

function units(state: GameState): Unit[] {
  return state.entities.filter((e): e is Unit => e.kind === 'unit');
}

function buildings(state: GameState): Building[] {
  return state.entities.filter((e): e is Building => e.kind === 'building');
}

export function playerUnits(state: GameState, p: PlayerIdx): Unit[] {
  return units(state).filter((u) => u.owner === p);
}

export function playerBuildings(state: GameState, p: PlayerIdx): Building[] {
  return buildings(state).filter((b) => b.owner === p);
}

function buildingCenter(b: Building): Vec2 {
  const s = BUILDINGS[b.type].size;
  return { x: b.pos.x + s / 2, y: b.pos.y + s / 2 };
}

export function entityCenter(e: Entity): Vec2 {
  return e.kind === 'building' ? buildingCenter(e as Building) : e.pos;
}

function hasTech(p: PlayerState, t: TechId): boolean {
  return p.techs.includes(t);
}

// ---------------------------------------------------------------------------
// Economy modifiers (faction + tech), the FinOps math
// ---------------------------------------------------------------------------

export function buildingBurnMult(p: PlayerState): number {
  let m = FACTIONS[p.faction].buildingBurnMult;
  if (hasTech(p, 'commitmentDiscount1')) m *= 0.85;
  if (hasTech(p, 'commitmentDiscount2')) m *= 0.85;
  return m;
}

export function serveTokenCost(state: GameState, p: PlayerState, u: Unit): number {
  let cost = ECONOMY.tokensPerServe * FACTIONS[p.faction].tokenCostMult;
  if (hasTech(p, 'promptCaching')) cost *= 0.7;
  if (hasTech(p, 'modelRouting')) cost *= 0.8;
  if (hasTech(p, 'speculativeDecoding')) cost *= 0.75; // Run capstone
  // cache layer aura
  for (const b of playerBuildings(state, u.owner)) {
    if (b.type === 'cacheLayer' && b.buildTicks === 0) {
      if (dist(buildingCenter(b), u.pos) <= ECONOMY.cacheAuraRadius) {
        cost *= ECONOMY.cacheAuraMult;
        break;
      }
    }
  }
  return cost;
}

export function serveRevenue(p: PlayerState): number {
  let rev = ECONOMY.revenuePerServe * FACTIONS[p.faction].revenueMult;
  if (hasTech(p, 'taggingPolicy')) rev *= 1.1;
  if (hasTech(p, 'unitEconomics')) rev *= 1.2;
  if (hasTech(p, 'batchInference')) rev *= 1.8;
  if (p.doctrine === 'rag') rev *= ECONOMY.ragRevenueMult;
  rev *= 1 + ECONOMY.fineTuneRevenueBonus * p.fineTuneJobs;
  // launch buzz: this player just pushed the global frontier
  if (p.buzzTicks > 0) rev *= FRONTIER.launchBuzzMult;
  // data-poisoning cyber attack: stale/corrupt context depresses revenue
  if (p.cyber && p.cyber.poisonedTicks > 0) rev *= CYBER.dataPoison.revenueMult;
  return rev;
}

/** Frontier repricing: a serve-agent's revenue multiplier for being behind
 *  the global frontier generation. Nothing about the agent changed — the
 *  market's valuation of it did. */
export function modelGenMult(state: GameState, u: Unit): number {
  const gap = Math.max(0, state.frontierGen - (u.gen ?? 0));
  return Math.max(FRONTIER.gapRevenueFloor, 1 - FRONTIER.gapRevenuePenalty * gap);
}

/** Premium segments pay their multiplier only to near-frontier models; a
 *  relegated agent still serves, at bulk pricing (downmarket, not dead). */
export function marketTierMult(state: GameState, m: Market, u: Unit): number {
  if (m.tier !== 'premium') return 1;
  return (u.gen ?? 0) >= state.frontierGen - FRONTIER.premiumGenSlack
    ? FRONTIER.premiumRevenueMult
    : 1;
}

/** Cost of this player's NEXT frontier training run. Escalates per target
 *  generation (scaling laws), discounted when the target generation already
 *  exists somewhere (fast-follow). Tokenless factions pay the token share in
 *  dollars, mirroring their per-invocation serve fee. */
export function frontierRunCost(
  state: GameState,
  p: PlayerState,
): { budget: number; data: number; tokens: number; targetGen: number; fastFollow: boolean } {
  const targetGen = p.modelGen + 1;
  // multiplicative loop, not Math.pow — keeps lockstep floats identical everywhere
  let growth = 1;
  for (let g = 1; g < targetGen; g++) growth *= FRONTIER.costGrowth;
  const fastFollow = targetGen <= state.frontierGen;
  if (fastFollow) growth *= FACTIONS[p.faction].fastFollowMult ?? FRONTIER.fastFollowMult;
  growth *= FACTIONS[p.faction].frontierCostMult ?? 1;
  const tokenless = Boolean(FACTIONS[p.faction].tokenless);
  const tokenShare = Math.round(FRONTIER.baseCostTokens * growth);
  return {
    budget:
      Math.round(FRONTIER.baseCostBudget * growth) +
      (tokenless ? Math.round(tokenShare * ECONOMY.serverlessDollarPerToken) : 0),
    data: Math.round(FRONTIER.baseCostData * growth),
    tokens: tokenless ? 0 : tokenShare,
    targetGen,
    fastFollow,
  };
}

/** True if the player has at least one fully-built (operational) building of
 *  the given type. Used for cyber prerequisites (attacker needs an Adversarial
 *  Lab; defender's SOC mitigates incoming ops). A DDoS'd building still counts
 *  as present — it's down, not gone. */
function hasOperationalBuilding(state: GameState, player: PlayerIdx, type: BuildingType): boolean {
  return playerBuildings(state, player).some((b) => b.type === type && b.buildTicks === 0);
}

export function servePeriodTicks(p: PlayerState): number {
  let sec = ECONOMY.servePeriodSec;
  if (hasTech(p, 'batchInference')) sec *= 1.4;
  return secToTicks(sec);
}

// ---------------------------------------------------------------------------
// Per-player transient sim bookkeeping (not part of serialized state shape
// consumers rely on — but kept ON the state so snapshots stay deterministic)
// ---------------------------------------------------------------------------

interface Research {
  tech: TechId;
  ticksLeft: number;
}

function researchLab(tech: TechId): BuildingType {
  return TECHS[tech].lab;
}

interface PhaseWork {
  ticksLeft: number;
}

// Stored in a parallel structure keyed by player index inside GameState via
// (state as any) — kept simple & serializable.
interface PlayerWork {
  /** active research jobs, at most one per lab building type */
  research: Research[];
  phaseWork: PhaseWork | null;
  fineTune: { buildingId: number; ticksLeft: number } | null;
  /** active frontier training run (Economy 3.0), at most one */
  trainingRun: { buildingId: number; ticksLeft: number } | null;
  brownedOut: boolean;
  tickRevenue: number;
  tickBurn: number;
  tickTokens: number;
  /** fog of war: tiles this player has ever had sight of. Kept in the sim so
   * BOTS and COPILOTS obey the same information rules as humans. Transient
   * (rebuilt from current positions after a snapshot restore). */
  explored: Uint8Array;
}

function work(state: GameState): PlayerWork[] {
  const s = state as GameState & { _work?: PlayerWork[] };
  if (!s._work || s._work.length !== state.players.length) {
    s._work = state.players.map(() => ({
      research: [],
      phaseWork: null,
      fineTune: null,
      trainingRun: null,
      brownedOut: false,
      tickRevenue: 0,
      tickBurn: 0,
      tickTokens: 0,
      explored: new Uint8Array(state.map.width * state.map.height),
    }));
  }
  return s._work;
}

export function activeResearch(
  state: GameState,
  p: PlayerIdx,
  lab?: BuildingType,
): { tech: TechId; ticksLeft: number } | null {
  const jobs = work(state)[p].research;
  if (lab) return jobs.find((r) => researchLab(r.tech) === lab) ?? null;
  return jobs[0] ?? null;
}

/** All active research jobs (one per lab type at most). */
export function activeResearchAll(
  state: GameState,
  p: PlayerIdx,
): { tech: TechId; ticksLeft: number }[] {
  return work(state)[p].research;
}

export function activePhaseWork(state: GameState, p: PlayerIdx): { ticksLeft: number } | null {
  return work(state)[p].phaseWork;
}

/** Has this player ever had sight of tile (x, y)? Sim-side fog of war. */
export function isExplored(state: GameState, player: PlayerIdx, x: number, y: number): boolean {
  const w = work(state)[player];
  const tx = Math.floor(x);
  const ty = Math.floor(y);
  if (!inBounds(state.map, tx, ty)) return false;
  return w.explored[ty * state.map.width + tx] === 1;
}

/** Mark everything within each entity's sight radius as explored. Runs once
 * per sim-second (fog granularity doesn't need tick precision). */
function updateExplored(state: GameState): void {
  const w = work(state);
  const mapW = state.map.width;
  for (const e of state.entities) {
    if (e.owner < 0) continue;
    if (!state.players[e.owner]?.alive) continue;
    let sight = 0;
    let c = e.pos;
    if (e.kind === 'unit') {
      sight = UNITS[(e as Unit).type].sight;
    } else if (e.kind === 'building') {
      const b = e as Building;
      sight = BUILDINGS[b.type].sight;
      c = buildingCenter(b);
    } else {
      continue;
    }
    const grid = w[e.owner].explored;
    const x0 = Math.max(0, Math.floor(c.x - sight));
    const x1 = Math.min(mapW - 1, Math.ceil(c.x + sight));
    const y0 = Math.max(0, Math.floor(c.y - sight));
    const y1 = Math.min(state.map.height - 1, Math.ceil(c.y + sight));
    const s2 = sight * sight;
    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        const dx = tx + 0.5 - c.x;
        const dy = ty + 0.5 - c.y;
        if (dx * dx + dy * dy <= s2) grid[ty * mapW + tx] = 1;
      }
    }
  }
}

export function activeFineTune(
  state: GameState,
  p: PlayerIdx,
): { buildingId: number; ticksLeft: number } | null {
  return work(state)[p].fineTune;
}

export function activeTrainingRun(
  state: GameState,
  p: PlayerIdx,
): { buildingId: number; ticksLeft: number } | null {
  return work(state)[p].trainingRun;
}

/** Data consumed per serve under the player's doctrine. */
export function serveDataCost(p: PlayerState): number {
  let c: number = ECONOMY.serveDataCost;
  if (p.doctrine === 'rag') c *= ECONOMY.ragDataCostMult;
  if (p.doctrine === 'finetune') c *= ECONOMY.fineTuneDataCostMult;
  return c;
}

/** Power throttle: 1 at healthy capacity, cap/draw (floored) in brownout. */
export function brownoutMult(p: PlayerState): number {
  if (p.powerDraw <= p.powerCap || p.powerDraw === 0) return 1;
  return Math.max(ECONOMY.brownoutFloor, p.powerCap / p.powerDraw);
}

// ---------------------------------------------------------------------------
// Map + game creation
// ---------------------------------------------------------------------------

function generateMap(rng: Rng, width: number, height: number): GameMap {
  const terrain = new Array<0 | 1>(width * height).fill(0);
  // scatter rock blobs, avoiding the corner start areas and center
  const blobs = Math.floor((width * height) / 180);
  for (let i = 0; i < blobs; i++) {
    const cx = rng.int(4, width - 5);
    const cy = rng.int(4, height - 5);
    // keep starts clear
    const nearCorner =
      (cx < 12 && cy < 12) ||
      (cx > width - 13 && cy > height - 13) ||
      (cx < 12 && cy > height - 13) ||
      (cx > width - 13 && cy < 12);
    const nearCenter = Math.abs(cx - width / 2) < 6 && Math.abs(cy - height / 2) < 6;
    if (nearCorner || nearCenter) continue;
    const size = rng.int(2, 4);
    for (let dy = 0; dy < size; dy++) {
      for (let dx = 0; dx < size; dx++) {
        if (rng.next() < 0.8) {
          const x = cx + dx;
          const y = cy + dy;
          if (x >= 0 && y >= 0 && x < width && y < height) {
            terrain[y * width + x] = 1;
          }
        }
      }
    }
  }
  return { width, height, terrain };
}

const START_CORNERS: Vec2[] = [
  { x: 5, y: 5 },
  { x: -10, y: -10 }, // negative = from far edge
  { x: 5, y: -10 },
  { x: -10, y: 5 },
];

function resolveCorner(c: Vec2, w: number, h: number): Vec2 {
  return { x: c.x >= 0 ? c.x : w + c.x, y: c.y >= 0 ? c.y : h + c.y };
}

export function createGame(config: GameConfig): GameState {
  // maps grow with the player count: 2p 48x48, 3p 58x58, 4p 68x68 —
  // four corners on a 48 was elbow-to-elbow
  const autoSize = Math.min(72, 48 + Math.max(0, config.players.length - 2) * 10);
  const width = config.mapWidth ?? autoSize;
  const height = config.mapHeight ?? autoSize;
  const rng = new Rng(config.seed);
  const map = generateMap(rng, width, height);

  const state: GameState = {
    tick: 0,
    rngState: 0,
    seed: config.seed,
    map,
    frontierGen: 0,
    players: config.players.map((pc) => ({
      name: pc.name,
      faction: pc.faction,
      isBot: pc.isBot,
      difficulty: pc.difficulty ?? 'normal',
      alive: true,
      budget: (config.startingBudget ?? ECONOMY.startingBudget) + FACTIONS[pc.faction].budgetBonus,
      tokens: ECONOMY.startingTokens,
      tokenCap: ECONOMY.baseTokenCap,
      data: 0,
      dataCap: ECONOMY.dataCapBase,
      powerCap: 0,
      powerDraw: 0,
      doctrine: null,
      cyber: { injectCd: 0, ddosCd: 0, poisonCd: 0, poisonedTicks: 0 },
      fineTuneJobs: 0,
      modelGen: 0,
      buzzTicks: 0,
      phase: 0,
      techs: [],
      insolvencyTicks: 0,
      revenuePerSec: 0,
      burnPerSec: 0,
      tokensPerSec: 0,
      stats: {
        unitsTrained: 0,
        unitsKilled: 0,
        unitsLost: 0,
        buildingsBuilt: 0,
        buildingsDestroyed: 0,
        buildingsLost: 0,
        dataHarvested: 0,
        tokensGenerated: 0,
        revenueEarned: 0,
        inferencesServed: 0,
        cyberOpsLaunched: 0,
      },
    })),
    entities: [],
    nextEntityId: 1,
    winner: null,
    events: [],
    timeline: {
      intervalSec: TIMELINE_INTERVAL_SEC,
      ticks: [],
      players: config.players.map(() => ({
        budget: [],
        revenue: [],
        army: [],
        kills: [],
        buildings: [],
        data: [],
        serves: [],
      })),
      events: [],
    },
  };

  const spawn = <T extends Entity>(e: Omit<T, 'id'>): T => {
    const ent = { ...e, id: state.nextEntityId++ } as T;
    state.entities.push(ent);
    return ent;
  };

  // players at corners
  config.players.forEach((pc, i) => {
    const c = resolveCorner(START_CORNERS[i % 4], width, height);
    const hq = BUILDINGS.hq;
    spawn<Building>({
      kind: 'building',
      type: 'hq',
      owner: i,
      pos: { x: c.x, y: c.y },
      hp: hq.hp,
      maxHp: hq.hp,
      buildTicks: 0,
      queue: [],
      trainTicks: 0,
    });
    // 3 starting engineers + 1 scout
    for (let k = 0; k < 3; k++) {
      spawn<Unit>({
        kind: 'unit',
        type: 'engineer',
        owner: i,
        pos: { x: c.x + 4 + k, y: c.y + 4 },
        hp: UNITS.engineer.hp,
        maxHp: UNITS.engineer.hp,
        order: { kind: 'idle' },
        path: [],
        attackCooldown: 0,
        carryingData: 0,
        anomalyTicks: 0,
        serving: false,
      });
    }
    spawn<Unit>({
      kind: 'unit',
      type: 'scoutCrawler',
      owner: i,
      pos: { x: c.x + 4, y: c.y + 5.5 },
      hp: UNITS.scoutCrawler.hp,
      maxHp: UNITS.scoutCrawler.hp,
      order: { kind: 'idle' },
      path: [],
      attackCooldown: 0,
      carryingData: 0,
      anomalyTicks: 0,
      serving: false,
    });
    // data node cluster near each start
    for (let k = 0; k < 2; k++) {
      spawn<DataNode>({
        kind: 'dataNode',
        owner: -1,
        pos: { x: c.x + 8 + rng.int(0, 2), y: c.y + rng.int(0, 3) },
        hp: 1,
        maxHp: 1,
        remaining: 600,
      });
    }
  });

  // center market cluster + midfield markets and data. The big center
  // segment is PREMIUM: it pays a multiplier, but only to near-frontier
  // models — the map-control prize doubles as the frontier-race prize.
  spawn<Market>({
    kind: 'market',
    owner: -1,
    pos: { x: width / 2, y: height / 2 },
    hp: 1,
    maxHp: 1,
    demand: 6,
    tier: 'premium',
    nextMoveTick: rng.int(ECONOMY.marketMoveMinSec, ECONOMY.marketMoveMaxSec) * TPS,
  });
  const mids: Vec2[] = [
    { x: width / 2, y: 8 },
    { x: width / 2, y: height - 8 },
    { x: 8, y: height / 2 },
    { x: width - 8, y: height / 2 },
  ];
  if (config.players.length >= 3) {
    // bigger maps get two extra segments at the open diagonal quarters
    mids.push({ x: width / 4, y: (3 * height) / 4 }, { x: (3 * width) / 4, y: height / 4 });
  }
  for (const m of mids) {
    spawn<Market>({
      kind: 'market',
      owner: -1,
      pos: m,
      hp: 1,
      maxHp: 1,
      demand: 3,
      nextMoveTick: rng.int(ECONOMY.marketMoveMinSec, ECONOMY.marketMoveMaxSec) * TPS,
    });
  }
  // midfield data nodes (density scales with map area)
  const midNodes = Math.max(6, Math.round((6 * width * height) / (48 * 48)));
  for (let k = 0; k < midNodes; k++) {
    spawn<DataNode>({
      kind: 'dataNode',
      owner: -1,
      pos: { x: rng.int(14, width - 14), y: rng.int(14, height - 14) },
      hp: 1,
      maxHp: 1,
      remaining: 900,
    });
  }

  state.rngState = rng.getState();
  work(state); // initialize
  updateExplored(state); // players start knowing their own base surroundings
  sampleTimeline(state); // graphs start at t=0
  return state;
}

// ---------------------------------------------------------------------------
// End-game telemetry
// ---------------------------------------------------------------------------

/** Sim seconds between end-game graph samples (~180 samples per 15-min cap). */
const TIMELINE_INTERVAL_SEC = 5;

function sampleTimeline(state: GameState): void {
  const tl = state.timeline;
  if (tl.ticks[tl.ticks.length - 1] === state.tick) return; // victory tick already sampled
  tl.ticks.push(state.tick);
  state.players.forEach((p, i) => {
    let army = 0;
    let buildings = 0;
    for (const e of state.entities) {
      if (e.owner !== i) continue;
      if (e.kind === 'unit') army++;
      else if (e.kind === 'building') buildings++;
    }
    const s = tl.players[i];
    s.budget.push(Math.round(p.budget));
    s.revenue.push(Math.round(p.revenuePerSec * 10) / 10);
    s.army.push(army);
    s.kills.push(p.stats.unitsKilled);
    s.buildings.push(buildings);
    s.data.push(Math.round(p.stats.dataHarvested));
    s.serves.push(p.stats.inferencesServed);
  });
}

/** Copy this tick's crucial GameEvents (already assembled by the sim for
 *  toasts/bots) into the persistent timeline so the end-game graphs can mark
 *  them. Runs every tick; state.events is transient and cleared next tick. */
function harvestTimelineEvents(state: GameState): void {
  for (const ev of state.events) {
    if (ev.player < 0) continue;
    const p = state.players[ev.player];
    let rec: { kind: TimelineEventKind; label: string } | null = null;
    switch (ev.type) {
      case 'phaseAdvanced':
        rec = { kind: 'phase', label: PHASE_NAMES[p.phase] };
        break;
      case 'doctrineChosen':
        rec = { kind: 'doctrine', label: p.doctrine === 'rag' ? 'RAG' : 'Fine-tune' };
        break;
      case 'modelShipped':
        rec = { kind: 'model', label: `Gen ${p.modelGen}` };
        break;
      case 'playerEliminated':
        rec = { kind: 'eliminated', label: ev.reason ?? 'Eliminated' };
        break;
    }
    if (rec) state.timeline.events.push({ tick: ev.tick, player: ev.player, ...rec });
  }
}

// ---------------------------------------------------------------------------
// Command application
// ---------------------------------------------------------------------------

function ownedUnits(state: GameState, p: PlayerIdx, ids: EntityId[]): Unit[] {
  const set = new Set(ids);
  return units(state).filter((u) => u.owner === p && set.has(u.id) && u.hp > 0);
}

function setPathTo(state: GameState, u: Unit, target: Vec2): void {
  u.path = findPath(state, u.pos, target);
}

function affordable(p: PlayerState, budget: number, data: number): boolean {
  return p.budget >= budget && p.data >= data;
}

export function applyCommand(state: GameState, player: PlayerIdx, cmd: Command): void {
  const p = state.players[player];
  if (!p || !p.alive || state.winner !== null) return;
  const factionCostMult = FACTIONS[p.faction].costMult;

  switch (cmd.type) {
    case 'move': {
      for (const u of ownedUnits(state, player, cmd.unitIds)) {
        u.order = { kind: 'move', target: { x: cmd.x, y: cmd.y } };
        u.serving = false;
        u.redeploy = false;
        setPathTo(state, u, { x: cmd.x, y: cmd.y });
      }
      break;
    }
    case 'stop': {
      for (const u of ownedUnits(state, player, cmd.unitIds)) {
        u.order = { kind: 'idle' };
        u.serving = false;
        u.redeploy = false;
        u.path = [];
      }
      break;
    }
    case 'gather': {
      const node = getEntity(state, cmd.nodeId);
      if (!node || node.kind !== 'dataNode') break;
      if (!isExplored(state, player, node.pos.x, node.pos.y)) break; // fog-honest
      for (const u of ownedUnits(state, player, cmd.unitIds)) {
        if (u.type !== 'engineer') continue;
        u.order = { kind: 'gather', targetId: node.id };
        u.serving = false;
        setPathTo(state, u, node.pos);
      }
      break;
    }
    case 'serve': {
      const market = getEntity(state, cmd.marketId);
      if (!market || market.kind !== 'market') break;
      if (!isExplored(state, player, market.pos.x, market.pos.y)) break; // fog-honest
      for (const u of ownedUnits(state, player, cmd.unitIds)) {
        if (!isServeUnit(u.type)) continue;
        u.order = { kind: 'serve', targetId: market.id };
        u.serving = false;
        u.redeploy = false;
        setPathTo(state, u, market.pos);
      }
      break;
    }
    case 'attack': {
      const target = getEntity(state, cmd.targetId);
      if (!target || (target.kind !== 'unit' && target.kind !== 'building')) break;
      if (target.owner === player) break;
      for (const u of ownedUnits(state, player, cmd.unitIds)) {
        if (!UNITS[u.type].attack) continue;
        if (UNITS[u.type].siege && target.kind === 'unit') continue; // siege can't hit units
        u.order = { kind: 'attack', targetId: target.id };
        u.serving = false;
        setPathTo(state, u, entityCenter(target));
      }
      break;
    }
    case 'build': {
      const stats = BUILDINGS[cmd.building];
      if (!stats) break;
      if (p.phase < stats.phase) break;
      if (!canFactionBuild(p.faction, cmd.building)) break;
      if (!buildingRequirementMet(state, player, cmd.building)) break;
      const cost = Math.round(stats.costBudget * factionCostMult);
      if (!affordable(p, cost, stats.costData)) break;
      const [eng] = ownedUnits(state, player, [cmd.unitId]);
      if (!eng || eng.type !== 'engineer') break;
      const x = Math.floor(cmd.x);
      const y = Math.floor(cmd.y);
      if (!canPlaceBuilding(state, cmd.building, x, y)) break;
      p.budget -= cost;
      p.data -= stats.costData;
      const b: Building = {
        id: state.nextEntityId++,
        kind: 'building',
        type: cmd.building,
        owner: player,
        pos: { x, y },
        hp: Math.max(1, Math.round(stats.hp * 0.1)),
        maxHp: stats.hp,
        buildTicks: secToTicks(stats.buildTimeSec * FACTIONS[p.faction].buildSpeedMult),
        queue: [],
        trainTicks: 0,
      };
      state.entities.push(b);
      eng.order = { kind: 'build', targetId: b.id };
      eng.serving = false;
      setPathTo(state, eng, buildingCenter(b));
      break;
    }
    case 'recycle': {
      const b = getEntity(state, cmd.buildingId);
      if (!b || b.kind !== 'building' || b.owner !== player) break;
      const bld = b as Building;
      if (bld.type === 'hq') break; // you don't sell the Cloud Console
      const bs = BUILDINGS[bld.type];
      const paid = Math.round(bs.costBudget * factionCostMult);
      // cancelling a construction site recoups most of it; tearing down a
      // finished building recoups half, scaled by remaining hp (a wreck
      // isn't worth much on the secondary market)
      const hpFrac = Math.max(0, bld.hp / bld.maxHp);
      const refund =
        bld.buildTicks > 0
          ? Math.round(paid * 0.75)
          : Math.round(paid * 0.5 * hpFrac);
      const dataRefund = bld.buildTicks > 0 ? bs.costData : Math.round(bs.costData * 0.5);
      p.budget += refund;
      p.data = Math.min(p.dataCap, p.data + dataRefund);
      // refund any units still in the training queue
      for (const qUnit of bld.queue) {
        p.budget += Math.round(UNITS[qUnit].costBudget * factionCostMult);
        p.data += UNITS[qUnit].costData;
      }
      state.entities = state.entities.filter((e) => e.id !== bld.id);
      break;
    }
    case 'repair': {
      const b = getEntity(state, cmd.buildingId);
      if (!b || b.kind !== 'building' || b.owner !== player) break;
      const bld = b as Building;
      if (bld.buildTicks === 0 && bld.hp >= bld.maxHp) break; // nothing to do
      for (const u of ownedUnits(state, player, cmd.unitIds)) {
        if (u.type !== 'engineer') continue;
        u.order = { kind: 'build', targetId: bld.id };
        u.serving = false;
        setPathTo(state, u, buildingCenter(bld));
      }
      break;
    }
    case 'train': {
      const b = getEntity(state, cmd.buildingId);
      if (!b || b.kind !== 'building' || b.owner !== player) break;
      const bld = b as Building;
      if (bld.buildTicks > 0) break;
      const bs = BUILDINGS[bld.type];
      if (!bs.trains.includes(cmd.unit)) break;
      const us = UNITS[cmd.unit];
      if (p.phase < us.phase) break;
      if (us.faction && us.faction !== p.faction) break; // faction-exclusive
      let cost = Math.round(us.costBudget * factionCostMult);
      if (hasTech(p, 'modelRouting')) cost = Math.round(cost * 0.85);
      let dataCost = us.costData;
      if (p.faction === 'openweights' && us.phase >= 1) dataCost += 10;
      if (!affordable(p, cost, dataCost)) break;
      if (bld.queue.length >= 5) break;
      p.budget -= cost;
      p.data -= dataCost;
      bld.queue.push(cmd.unit);
      if (bld.queue.length === 1) {
        bld.trainTicks = secToTicks(us.trainTimeSec * FACTIONS[p.faction].buildSpeedMult);
      }
      break;
    }
    case 'cancelTrain': {
      const b = getEntity(state, cmd.buildingId);
      if (!b || b.kind !== 'building' || b.owner !== player) break;
      const bld = b as Building;
      const t = bld.queue.pop();
      if (t) {
        // refund
        p.budget += Math.round(UNITS[t].costBudget * factionCostMult);
        p.data += UNITS[t].costData;
        if (bld.queue.length === 0) bld.trainTicks = 0;
      }
      break;
    }
    case 'research': {
      const ts = TECHS[cmd.tech];
      if (!ts) break;
      if (p.phase < ts.phase) break;
      if (hasTech(p, cmd.tech)) break;
      if (ts.requires && !hasTech(p, ts.requires)) break;
      if (ts.notFaction && p.faction === ts.notFaction) break; // innate/blocked
      const w = work(state)[player];
      if (w.research.some((r) => researchLab(r.tech) === ts.lab)) break; // lab busy
      // needs an operational lab
      const lab = playerBuildings(state, player).find(
        (b) => b.type === ts.lab && b.buildTicks === 0,
      );
      if (!lab) break;
      if (!affordable(p, ts.costBudget, ts.costData)) break;
      p.budget -= ts.costBudget;
      p.data -= ts.costData;
      w.research.push({ tech: cmd.tech, ticksLeft: secToTicks(ts.researchTimeSec) });
      break;
    }
    case 'chooseDoctrine': {
      if (p.doctrine !== null) break; // permanent — no take-backs
      if (cmd.doctrine !== 'rag' && cmd.doctrine !== 'finetune') break;
      p.doctrine = cmd.doctrine;
      state.events.push({
        tick: state.tick,
        player,
        type: 'doctrineChosen',
        message: `${p.name} committed to the ${cmd.doctrine === 'rag' ? 'RAG' : 'Fine-Tuning'} doctrine.`,
      });
      break;
    }
    case 'fineTune': {
      if (p.doctrine !== 'finetune') break;
      if (p.fineTuneJobs >= ECONOMY.fineTuneMaxJobs) break;
      const w2 = work(state)[player];
      if (w2.fineTune) break; // one job at a time
      const b = getEntity(state, cmd.buildingId);
      if (!b || b.kind !== 'building' || b.owner !== player) break;
      const bld = b as Building;
      if (bld.type !== 'trainingCluster' || bld.buildTicks > 0) break;
      if (p.data < ECONOMY.fineTuneCostData || p.tokens < ECONOMY.fineTuneCostTokens) break;
      p.data -= ECONOMY.fineTuneCostData;
      p.tokens -= ECONOMY.fineTuneCostTokens;
      w2.fineTune = { buildingId: bld.id, ticksLeft: secToTicks(ECONOMY.fineTuneTimeSec) };
      break;
    }
    case 'trainingRun': {
      const w3 = work(state)[player];
      if (w3.trainingRun) break; // one run at a time
      const b = getEntity(state, cmd.buildingId);
      if (!b || b.kind !== 'building' || b.owner !== player) break;
      const bld = b as Building;
      if (bld.type !== 'trainingCluster' || bld.buildTicks > 0) break;
      const cost = frontierRunCost(state, p);
      if (p.budget < cost.budget || p.data < cost.data || p.tokens < cost.tokens) break;
      p.budget -= cost.budget;
      p.data -= cost.data;
      p.tokens -= cost.tokens;
      w3.trainingRun = { buildingId: bld.id, ticksLeft: secToTicks(FRONTIER.runTimeSec) };
      break;
    }
    case 'advancePhase': {
      if (p.phase >= 2) break;
      const w = work(state)[player];
      if (w.phaseWork) break;
      const ps = PHASE_ADVANCE[p.phase];
      if (!affordable(p, ps.costBudget, ps.costData)) break;
      p.budget -= ps.costBudget;
      p.data -= ps.costData;
      w.phaseWork = { ticksLeft: secToTicks(ps.timeSec) };
      break;
    }
    case 'setStance': {
      if (cmd.stance !== 'aggressive' && cmd.stance !== 'defend' && cmd.stance !== 'passive') break;
      for (const u of ownedUnits(state, player, cmd.unitIds)) {
        u.stance = cmd.stance;
        if (cmd.stance === 'defend') u.anchor = { x: u.pos.x, y: u.pos.y };
      }
      break;
    }
    case 'fixAnomaly': {
      const [u] = ownedUnits(state, player, [cmd.unitId]);
      if (u && u.anomalyTicks > 0) {
        u.anomalyTicks = 0;
        state.events.push({
          tick: state.tick,
          player,
          type: 'anomalyCleared',
          message: 'Runaway loop terminated manually.',
          entityId: u.id,
        });
      }
      break;
    }
    case 'promptInjection':
    case 'ddos':
    case 'dataPoison': {
      applyCyberOp(state, player, cmd.type, cmd.targetId);
      break;
    }
    case 'guard': {
      const target = getEntity(state, cmd.targetId);
      if (!target || (target.kind !== 'unit' && target.kind !== 'building')) break;
      if (target.owner !== player) break; // you guard your own
      for (const u of ownedUnits(state, player, cmd.unitIds)) {
        if (!UNITS[u.type].attack || u.type === 'engineer') continue; // combat units only
        if (u.id === target.id) continue; // can't guard yourself
        u.order = { kind: 'guard', targetId: target.id };
        u.serving = false;
        setPathTo(state, u, entityCenter(target));
      }
      break;
    }
    case 'patrol': {
      const a = { x: cmd.ax, y: cmd.ay };
      const b = { x: cmd.bx, y: cmd.by };
      for (const u of ownedUnits(state, player, cmd.unitIds)) {
        if (!UNITS[u.type].attack || u.type === 'engineer') continue; // combat units only
        u.order = { kind: 'patrol', patrolA: a, patrolB: b, patrolToB: true };
        u.serving = false;
        setPathTo(state, u, b);
      }
      break;
    }
    case 'surrender': {
      eliminate(state, player, 'surrendered');
      break;
    }
  }
}

/** Resolve and apply one cyber operation. Shared preconditions: the attacker
 *  has an operational Adversarial Lab, the target is an enemy's operational
 *  building, the ability is off cooldown, and the cost is affordable. Effects
 *  are reduced by the victim's Security Operations Center and matching tech. */
function applyCyberOp(
  state: GameState,
  player: PlayerIdx,
  op: 'promptInjection' | 'ddos' | 'dataPoison',
  targetId: EntityId,
): void {
  const p = state.players[player];
  const target = getEntity(state, targetId);
  if (!target || target.kind !== 'building') return;
  const victimIdx = target.owner;
  if (victimIdx === player || victimIdx < 0) return;
  const bld = target as Building;
  if (bld.buildTicks > 0) return; // can't hit a construction site
  const victim = state.players[victimIdx];
  if (!victim || !victim.alive) return;
  if (!hasOperationalBuilding(state, player, 'adversarialLab')) return;
  // Phase gate: heavier attacks unlock later (see CYBER.*.phase).
  if (p.phase < CYBER[op].phase) return;

  const socMult = hasOperationalBuilding(state, victimIdx, 'socCenter') ? CYBER.socResistMult : 1;
  const buildingName = BUILDINGS[bld.type].name;

  if (op === 'promptInjection') {
    const cfg = CYBER.promptInjection;
    if (p.cyber.injectCd > 0) return;
    if (!affordable(p, cfg.costBudget, cfg.costData)) return;
    p.budget -= cfg.costBudget;
    p.data -= cfg.costData;
    p.cyber.injectCd = secToTicks(cfg.cooldownSec);
    p.stats.cyberOpsLaunched++;
    // How many nearby enemy serve-agents get jailbroken into a runaway loop.
    let count: number = cfg.maxTargets;
    count = Math.ceil(count * socMult);
    if (hasTech(victim, 'inputSanitization')) count = Math.ceil(count * CYBER.sanitizeResistMult);
    const center = buildingCenter(bld);
    const agents = playerUnits(state, victimIdx)
      .filter((u) => isServeUnit(u.type) && u.anomalyTicks === 0 && dist(u.pos, center) <= cfg.radius)
      .sort((a, b) => a.id - b.id); // deterministic pick
    let hit = 0;
    for (const u of agents.slice(0, count)) {
      u.anomalyTicks = secToTicks(ECONOMY.anomalyDurationSec);
      hit++;
    }
    if (hit > 0) {
      state.events.push({
        tick: state.tick,
        player: victimIdx,
        type: 'cyberAttack',
        message: `Prompt injection! ${hit} of your agents near ${buildingName} jailbroke into runaway loops.`,
        entityId: bld.id,
      });
    } else {
      state.events.push({
        tick: state.tick,
        player: victimIdx,
        type: 'cyberDefended',
        message: `Prompt injection near ${buildingName} was sanitized — no agents compromised.`,
        entityId: bld.id,
      });
    }
    return;
  }

  if (op === 'ddos') {
    const cfg = CYBER.ddos;
    if (p.cyber.ddosCd > 0) return;
    if (!affordable(p, cfg.costBudget, cfg.costData)) return;
    p.budget -= cfg.costBudget;
    p.data -= cfg.costData;
    p.cyber.ddosCd = secToTicks(cfg.cooldownSec);
    p.stats.cyberOpsLaunched++;
    let durSec = cfg.durationSec * socMult;
    if (hasTech(victim, 'rateLimiting')) durSec *= CYBER.wafResistMult;
    const ticks = Math.round(secToTicks(durSec));
    if (ticks >= 1) {
      bld.disabledTicks = Math.max(bld.disabledTicks ?? 0, ticks);
      state.events.push({
        tick: state.tick,
        player: victimIdx,
        type: 'cyberAttack',
        message: `DDoS! Your ${buildingName} is offline for ${Math.round(durSec)}s.`,
        entityId: bld.id,
      });
    } else {
      state.events.push({
        tick: state.tick,
        player: victimIdx,
        type: 'cyberDefended',
        message: `Your WAF absorbed a DDoS on ${buildingName}.`,
        entityId: bld.id,
      });
    }
    return;
  }

  // dataPoison
  const cfg = CYBER.dataPoison;
  if (p.cyber.poisonCd > 0) return;
  if (!affordable(p, cfg.costBudget, cfg.costData)) return;
  p.budget -= cfg.costBudget;
  p.data -= cfg.costData;
  p.cyber.poisonCd = secToTicks(cfg.cooldownSec);
  p.stats.cyberOpsLaunched++;
  let drain = cfg.drainData * socMult;
  let durSec = cfg.durationSec * socMult;
  if (hasTech(victim, 'dataProvenance')) {
    drain *= CYBER.provenanceResistMult;
    durSec *= CYBER.provenanceResistMult;
  }
  const scrubbed = Math.min(victim.data, Math.round(drain));
  victim.data -= scrubbed;
  const ticks = Math.round(secToTicks(durSec));
  victim.cyber.poisonedTicks = Math.max(victim.cyber.poisonedTicks, ticks);
  state.events.push({
    tick: state.tick,
    player: victimIdx,
    type: 'cyberAttack',
    message: `Data poisoning! ${scrubbed} data scrubbed and revenue depressed for ${Math.round(durSec)}s.`,
    entityId: bld.id,
  });
}

// ---------------------------------------------------------------------------
// Tick systems
// ---------------------------------------------------------------------------

function eliminate(state: GameState, player: PlayerIdx, why: string): void {
  const p = state.players[player];
  if (!p.alive) return;
  p.alive = false;
  // torch their entities
  state.entities = state.entities.filter((e) => e.owner !== player);
  state.events.push({
    tick: state.tick,
    player,
    type: 'playerEliminated',
    message: `${p.name} is out (${why}).`,
    reason: why,
  });
}

function findDropoff(state: GameState, u: Unit): Building | null {
  let best: Building | null = null;
  let bestD = Infinity;
  for (const b of playerBuildings(state, u.owner)) {
    if (b.buildTicks > 0) continue;
    if (b.type !== 'hq' && b.type !== 'dataWarehouse') continue;
    const d = dist(buildingCenter(b), u.pos);
    if (d < bestD) {
      bestD = d;
      best = b;
    }
  }
  return best;
}

function moveAlongPath(state: GameState, u: Unit, speedTilesPerSec: number): boolean {
  const step = speedTilesPerSec / TPS;
  let remaining = step;
  while (remaining > 0 && u.path.length > 0) {
    const wp = u.path[0];
    const d = dist(u.pos, wp);
    if (d <= remaining) {
      u.pos = { x: wp.x, y: wp.y };
      u.path.shift();
      remaining -= d;
    } else {
      u.pos = {
        x: u.pos.x + ((wp.x - u.pos.x) / d) * remaining,
        y: u.pos.y + ((wp.y - u.pos.y) / d) * remaining,
      };
      remaining = 0;
    }
  }
  return u.path.length === 0;
}

/** transient per-unit gather timers, kept on the unit via a serializable field */
interface GatherState {
  miningTicks: number;
}

function gatherState(u: Unit): GatherState {
  const uu = u as Unit & { _gather?: GatherState };
  if (!uu._gather) uu._gather = { miningTicks: 0 };
  return uu._gather;
}

interface ServeState {
  cooldown: number;
}

function serveState(u: Unit): ServeState {
  const uu = u as Unit & { _serve?: ServeState };
  if (!uu._serve) uu._serve = { cooldown: 0 };
  return uu._serve;
}

function tickUnits(state: GameState): void {
  const w = work(state);

  // ephemeral units (Burst Workers) reach their TTL and despawn
  state.entities = state.entities.filter(
    (e) => !(e.kind === 'unit' && (e as Unit).expiresTick !== undefined && state.tick >= (e as Unit).expiresTick!),
  );

  // Pre-compute serve slot allocation per market: first-come by entity id.
  const serveSlots = new Map<EntityId, EntityId[]>(); // market -> unit ids granted a slot
  for (const m of state.entities) {
    if (m.kind !== 'market') continue;
    const servers = units(state)
      .filter(
        (u) =>
          u.order.kind === 'serve' &&
          u.order.targetId === m.id &&
          dist(u.pos, m.pos) <= 3.2 &&
          u.hp > 0,
      )
      .sort((a, b) => a.id - b.id)
      .slice(0, (m as Market).demand)
      .map((u) => u.id);
    serveSlots.set(m.id, servers);
  }

  for (const u of units(state)) {
    if (u.hp <= 0) continue;
    const p = state.players[u.owner];
    if (!p.alive) continue;
    const us = UNITS[u.type];

    // idle token drain (idle inference is not free — except serverless,
    // whose whole identity is scaling to zero)
    let idleDrain = FACTIONS[p.faction].tokenless ? 0 : us.idleTokensPerSec / TPS;
    if (hasTech(p, 'autoscaling')) idleDrain *= 0.4;
    if (u.anomalyTicks > 0) {
      idleDrain += (ECONOMY.anomalyTokenMult * us.idleTokensPerSec + 2) / TPS;
      u.anomalyTicks--;
      if (u.anomalyTicks === 0) {
        state.events.push({
          tick: state.tick,
          player: u.owner,
          type: 'anomalyCleared',
          message: 'Runaway loop burned itself out.',
          entityId: u.id,
        });
      }
    }
    p.tokens = Math.max(0, p.tokens - idleDrain);
    w[u.owner].tickTokens -= idleDrain;

    if (u.attackCooldown > 0) u.attackCooldown--;

    switch (u.order.kind) {
      case 'idle':
        if (u.redeploy && isServeUnit(u.type) && state.tick % TPS === 0) {
          // stranded by a market migration: head to the nearest DISCOVERED
          // market with a free demand slot the moment one is known
          const candidates = state.entities
            .filter(
              (e): e is Market =>
                e.kind === 'market' && isExplored(state, u.owner, e.pos.x, e.pos.y),
            )
            .map((m) => ({
              m,
              servers: units(state).filter(
                (o) => o.order.kind === 'serve' && o.order.targetId === m.id,
              ).length,
              d: dist(u.pos, m.pos),
            }))
            .filter((c) => c.servers < c.m.demand)
            .sort((a, b) => a.d - b.d);
          if (candidates.length > 0) {
            u.order = { kind: 'serve', targetId: candidates[0].m.id };
            u.redeploy = false;
            setPathTo(state, u, candidates[0].m.pos);
          }
          break;
        }
        if (u.type === 'engineer') {
          // idle engineers standing near an unfinished site pitch in
          // (short radius so deliberately pulled-away workers stay pulled)
          assistNearbyConstruction(state, u, 6);
        } else {
          // auto-acquire hostiles in range for combat units
          autoAcquire(state, u);
        }
        break;

      case 'move': {
        if (moveAlongPath(state, u, us.speed)) {
          u.order = { kind: 'idle' };
        }
        break;
      }

      case 'gather': {
        const node = getEntity(state, u.order.targetId!) as DataNode | undefined;
        if (!node || node.kind !== 'dataNode' || node.remaining <= 0) {
          // find another node nearby
          const next = state.entities
            .filter((e): e is DataNode => e.kind === 'dataNode' && e.remaining > 0)
            .sort((a, b) => dist(a.pos, u.pos) - dist(b.pos, u.pos))[0];
          if (next) {
            u.order = { kind: 'gather', targetId: next.id };
            setPathTo(state, u, next.pos);
          } else {
            u.order = { kind: 'idle' };
          }
          break;
        }
        if (u.carryingData >= ECONOMY.gatherCarry) {
          u.order = { kind: 'returnData', targetId: node.id };
          const drop = findDropoff(state, u);
          if (drop) setPathTo(state, u, buildingCenter(drop));
          break;
        }
        if (dist(u.pos, node.pos) > 1.6) {
          if (moveAlongPath(state, u, us.speed) && dist(u.pos, node.pos) > 1.6) {
            setPathTo(state, u, node.pos); // path went stale
          }
        } else {
          const gs = gatherState(u);
          gs.miningTicks++;
          const perTick =
            (ECONOMY.gatherCarry / secToTicks(ECONOMY.gatherTimeSec)) *
            (FACTIONS[p.faction].gatherRateMult ?? 1);
          const take = Math.min(perTick, node.remaining);
          u.carryingData += take;
          node.remaining -= take;
          if (node.remaining <= 0) {
            state.entities = state.entities.filter((e) => e.id !== node.id);
          }
        }
        break;
      }

      case 'returnData': {
        const drop = findDropoff(state, u);
        if (!drop) {
          u.order = { kind: 'idle' };
          break;
        }
        if (dist(u.pos, buildingCenter(drop)) > BUILDINGS[drop.type].size / 2 + 1.2) {
          if (moveAlongPath(state, u, us.speed)) {
            setPathTo(state, u, buildingCenter(drop));
          }
        } else {
          p.data = Math.min(p.dataCap, p.data + u.carryingData);
          p.stats.dataHarvested += u.carryingData;
          u.carryingData = 0;
          // head back to the node we came from (or nearest)
          const nodeId = u.order.targetId;
          const node = nodeId ? getEntity(state, nodeId) : undefined;
          if (node && node.kind === 'dataNode') {
            u.order = { kind: 'gather', targetId: node.id };
            setPathTo(state, u, node.pos);
          } else {
            u.order = { kind: 'gather', targetId: -1 };
          }
        }
        break;
      }

      case 'serve': {
        const m = getEntity(state, u.order.targetId!) as Market | undefined;
        if (!m || m.kind !== 'market') {
          u.order = { kind: 'idle' };
          u.serving = false;
          break;
        }
        if (dist(u.pos, m.pos) > 3.0) {
          u.serving = false;
          if (moveAlongPath(state, u, us.speed) && dist(u.pos, m.pos) > 3.0) {
            setPathTo(state, u, m.pos);
          }
          break;
        }
        const slots = serveSlots.get(m.id) ?? [];
        const hasSlot = slots.includes(u.id);
        u.serving = hasSlot;
        if (!hasSlot) break; // demand saturated — waiting
        if (u.anomalyTicks > 0) break; // anomalous agents earn nothing
        const ss = serveState(u);
        if (ss.cooldown > 0) {
          ss.cooldown--;
          break;
        }
        const costMult = us.serveCostMult ?? 1;
        const tokenCost = serveTokenCost(state, p, u) * costMult;
        if (FACTIONS[p.faction].tokenless) {
          // serverless: no token pool — every serve pays a per-invocation
          // fee in dollars (pure OpEx; still shaped by caching/routing techs)
          const fee = tokenCost * ECONOMY.serverlessDollarPerToken;
          p.budget -= fee;
          w[u.owner].tickBurn += fee;
        } else {
          if (p.tokens < tokenCost) break; // token-starved: throttled
          p.tokens -= tokenCost;
          w[u.owner].tickTokens -= tokenCost;
        }
        // serving consumes data (context freshness); an empty pool means a
        // stale model that customers pay far less for
        const dCost = serveDataCost(p) * costMult;
        let freshness = 1;
        if (p.data >= dCost) {
          p.data -= dCost;
        } else {
          freshness = ECONOMY.staleRevenueMult;
        }
        // saturated markets pay less per serve for everyone; the frontier
        // race reprices the agent (gen gap) and gates premium-tier payouts
        const rev =
          serveRevenue(p) *
          (us.serveRevenueMult ?? 1) *
          marketSaturationMult(slots.length) *
          freshness *
          modelGenMult(state, u) *
          marketTierMult(state, m, u);
        p.budget += rev;
        p.stats.inferencesServed++;
        w[u.owner].tickRevenue += rev;
        ss.cooldown = Math.max(1, Math.round(servePeriodTicks(p) * (us.servePeriodMult ?? 1)));
        break;
      }

      case 'attack': {
        const target = getEntity(state, u.order.targetId!);
        if (!target || target.hp <= 0 || (target.kind !== 'unit' && target.kind !== 'building')) {
          u.order = { kind: 'idle' };
          break;
        }
        // defend stance: leash back to the anchor instead of chasing forever
        if ((u.stance ?? 'aggressive') === 'defend' && u.anchor && dist(u.pos, u.anchor) > 9) {
          u.order = { kind: 'move', target: { x: u.anchor.x, y: u.anchor.y } };
          setPathTo(state, u, u.anchor);
          break;
        }
        const atk = us.attack;
        if (!atk) {
          u.order = { kind: 'idle' };
          break;
        }
        const tc = entityCenter(target);
        const range =
          target.kind === 'building'
            ? atk.range + BUILDINGS[(target as Building).type].size / 2
            : atk.range;
        if (dist(u.pos, tc) > range) {
          if (moveAlongPath(state, u, us.speed) && dist(u.pos, tc) > range) {
            setPathTo(state, u, tc);
          }
        } else if (u.attackCooldown === 0) {
          dealDamage(state, u, target);
          u.attackCooldown = secToTicks(atk.periodSec);
        }
        break;
      }

      case 'build': {
        const b = getEntity(state, u.order.targetId!) as Building | undefined;
        if (!b || b.kind !== 'building' || b.owner !== u.owner) {
          u.order = { kind: 'idle' };
          break;
        }
        if (b.buildTicks === 0 && b.hp >= b.maxHp) {
          // done here — chain to the next nearby job, if any
          assistNearbyConstruction(state, u);
          break;
        }
        const c = buildingCenter(b);
        if (dist(u.pos, c) > BUILDINGS[b.type].size / 2 + 1.4) {
          if (moveAlongPath(state, u, us.speed)) setPathTo(state, u, c);
        } else if (b.buildTicks > 0) {
          // construction
          b.buildTicks--;
          b.hp = Math.min(b.maxHp, b.hp + b.maxHp / secToTicks(BUILDINGS[b.type].buildTimeSec));
          if (b.buildTicks === 0) {
            b.hp = b.maxHp;
            state.players[b.owner].stats.buildingsBuilt++;
            assistNearbyConstruction(state, u);
          }
        } else {
          // repair: same pace as construction, costs 25% of build price
          // pro-rated per hp — fixing infrastructure isn't free
          const bs = BUILDINGS[b.type];
          const ticks = secToTicks(bs.buildTimeSec);
          const heal = b.maxHp / ticks;
          const costPerTick = (0.25 * bs.costBudget) / ticks;
          if (p.budget > costPerTick) {
            p.budget -= costPerTick;
            w[u.owner].tickBurn += costPerTick;
            b.hp = Math.min(b.maxHp, b.hp + heal);
            if (b.hp >= b.maxHp) assistNearbyConstruction(state, u);
          }
          // out of budget: engineer waits at the site
        }
        break;
      }

      case 'guard': {
        const g = getEntity(state, u.order.targetId!);
        if (!g || (g.kind !== 'unit' && g.kind !== 'building') || g.owner !== u.owner || g.hp <= 0) {
          u.order = { kind: 'idle' };
          break;
        }
        const atk = us.attack;
        const aggro = atk ? Math.max(atk.range + 2, us.sight - 1) : us.sight - 1;
        // fight off anything threatening the escort, then fall back in
        if (atk && combatEngage(state, u, aggro)) break;
        // no threat: follow the guarded entity, holding a small standoff
        const gc = entityCenter(g);
        const followDist = 3;
        if (dist(u.pos, gc) > followDist) {
          if (moveAlongPath(state, u, us.speed) && dist(u.pos, gc) > followDist) setPathTo(state, u, gc);
        } else {
          u.path = []; // in position — hold
        }
        break;
      }

      case 'patrol': {
        const a = u.order.patrolA;
        const b = u.order.patrolB;
        if (!a || !b) {
          u.order = { kind: 'idle' };
          break;
        }
        const atk = us.attack;
        const aggro = atk ? Math.max(atk.range + 2, us.sight - 1) : us.sight - 1;
        // attack-move: engage anything on the route, then resume the leg
        if (atk && combatEngage(state, u, aggro)) break;
        const dest = u.order.patrolToB ? b : a;
        if (dist(u.pos, dest) <= 1.4) {
          // reached this end — turn around toward the other point
          u.order.patrolToB = !u.order.patrolToB;
          setPathTo(state, u, u.order.patrolToB ? b : a);
        } else if (moveAlongPath(state, u, us.speed) && dist(u.pos, dest) > 1.4) {
          setPathTo(state, u, dest); // path emptied early (e.g. after combat) — re-path
        }
        break;
      }
    }
  }
}

/** After finishing a job (or while idling next to a site), engineers pick up
 * the nearest unfinished construction — and, when chaining from a completed
 * job, nearby repairs too — instead of standing around billing hours. */
function assistNearbyConstruction(state: GameState, u: Unit, radius = 12): void {
  let best: Building | null = null;
  let bestD = Infinity;
  for (const b of playerBuildings(state, u.owner)) {
    if (b.buildTicks === 0) continue; // auto-chain only to constructions;
    // damage repair stays a deliberate (manual or copilot) spend decision
    const d = dist(u.pos, buildingCenter(b));
    if (d <= radius && d < bestD) {
      bestD = d;
      best = b;
    }
  }
  if (best) {
    u.order = { kind: 'build', targetId: best.id };
    setPathTo(state, u, buildingCenter(best));
  } else {
    u.order = { kind: 'idle' };
  }
}

function autoAcquire(state: GameState, u: Unit): void {
  const us = UNITS[u.type];
  const atk = us.attack;
  if (!atk) return;
  if (u.type === 'engineer') return; // engineers don't pick fights
  if (u.type === 'scoutCrawler') return; // pestering is a choice, not a reflex
  if ((u.stance ?? 'aggressive') === 'passive') return;
  const aggro = Math.max(atk.range + 2, us.sight - 1);
  let best: Entity | null = null;
  let bestD = Infinity;
  for (const e of state.entities) {
    if (e.owner === u.owner || e.owner === -1) continue;
    if (e.kind !== 'unit' && e.kind !== 'building') continue;
    if (us.siege && e.kind !== 'building') continue; // siege only sees buildings
    if (!state.players[e.owner]?.alive) continue;
    const d = dist(u.pos, entityCenter(e));
    if (d < bestD && d <= aggro) {
      bestD = d;
      best = e;
    }
  }
  if (best) {
    u.order = { kind: 'attack', targetId: best.id };
    setPathTo(state, u, entityCenter(best));
  }
}

/** Engage the nearest enemy within `aggro` tiles WITHOUT changing the unit's
 * order — moves into range and fires this tick if able. Used by guard/patrol,
 * which own their order and just borrow the combat step. Returns true if a
 * target was engaged (so the caller skips its own movement this tick). */
function combatEngage(state: GameState, u: Unit, aggro: number): boolean {
  const us = UNITS[u.type];
  const atk = us.attack;
  if (!atk) return false;
  let best: Entity | null = null;
  let bestD = Infinity;
  for (const e of state.entities) {
    if (e.owner === u.owner || e.owner === -1) continue;
    if (e.kind !== 'unit' && e.kind !== 'building') continue;
    if (us.siege && e.kind !== 'building') continue; // siege only hits buildings
    if (!state.players[e.owner]?.alive) continue;
    const d = dist(u.pos, entityCenter(e));
    if (d < bestD && d <= aggro) {
      bestD = d;
      best = e;
    }
  }
  if (!best) return false;
  const tc = entityCenter(best);
  const range =
    best.kind === 'building' ? atk.range + BUILDINGS[(best as Building).type].size / 2 : atk.range;
  if (dist(u.pos, tc) > range) {
    if (moveAlongPath(state, u, us.speed) && dist(u.pos, tc) > range) setPathTo(state, u, tc);
  } else if (u.attackCooldown === 0) {
    dealDamage(state, u, best);
    u.attackCooldown = secToTicks(atk.periodSec);
  }
  return true;
}

function dealDamage(state: GameState, attacker: Unit | Building, target: Entity): void {
  const atk =
    attacker.kind === 'unit'
      ? UNITS[(attacker as Unit).type].attack!
      : BUILDINGS[(attacker as Building).type].attack!;
  let damage = atk.damage;
  // Test-Time Compute (Run capstone): your combat units hit 25% harder.
  if (attacker.kind === 'unit' && hasTech(state.players[attacker.owner], 'testTimeCompute')) {
    damage *= 1.25;
  }
  target.hp -= damage;

  // Being shot at is not a spectator sport: armed victims retaliate,
  // unarmed ones flee home — unless already fighting or told otherwise.
  if (target.kind === 'unit' && target.hp > 0) {
    const tu = target as Unit;
    // "Busy" orders are deliberate and must not be hijacked by retaliation: a
    // unit already attacking, one the player is actively moving/retreating,
    // and guard/patrol units (which run their own engagement logic) all keep
    // their order. This is what lets you pull a unit OUT of a fight — a plain
    // move now sticks even under fire.
    const busy =
      tu.order.kind === 'attack' ||
      tu.order.kind === 'move' ||
      tu.order.kind === 'guard' ||
      tu.order.kind === 'patrol';
    const canFightBack =
      UNITS[tu.type].attack &&
      !(UNITS[tu.type].siege && attacker.kind === 'unit') &&
      (tu.stance ?? 'aggressive') !== 'passive';
    // helpless units flee home; armed-but-passive units hold position
    // (passive is an explicit order, not cowardice)
    const helpless =
      !UNITS[tu.type].attack || (UNITS[tu.type].siege && attacker.kind === 'unit');
    if (!busy) {
      if (canFightBack) {
        tu.order = { kind: 'attack', targetId: attacker.id };
        tu.serving = false;
        tu.path = findPath(state, tu.pos, entityCenter(attacker));
      } else if (helpless) {
        const home = playerBuildings(state, tu.owner).find((b) => b.type === 'hq');
        if (home && tu.order.kind !== 'move') {
          tu.order = { kind: 'move', target: buildingCenter(home) };
          tu.serving = false;
          tu.path = findPath(state, tu.pos, buildingCenter(home));
        }
      }
    }
  }
  // notify defender occasionally
  if (state.tick % (TPS * 5) === 0 || target.hp <= 0) {
    state.events.push({
      tick: state.tick,
      player: target.owner,
      type: 'underAttack',
      message: 'Your infrastructure is under attack!',
      entityId: target.id,
    });
  }
  if (target.hp <= 0) {
    state.entities = state.entities.filter((e) => e.id !== target.id);
    if (target.owner >= 0 && target.owner !== attacker.owner) {
      const killer = state.players[attacker.owner].stats;
      const victim = state.players[target.owner].stats;
      if (target.kind === 'unit') {
        killer.unitsKilled++;
        victim.unitsLost++;
      } else if (target.kind === 'building') {
        killer.buildingsDestroyed++;
        victim.buildingsLost++;
      }
    }
    if (target.kind === 'building' && (target as Building).type === 'hq') {
      // losing the HQ doesn't instantly eliminate — but with no console you
      // can't rebuild a practice. Eliminate if no other HQ.
      const owner = target.owner;
      const stillHasHq = playerBuildings(state, owner).some((b) => b.type === 'hq');
      if (!stillHasHq) eliminate(state, owner, 'Cloud Console destroyed');
    }
  }
}

function tickBuildings(state: GameState): void {
  const w = work(state);

  // Recompute the power grid: operational buildings only. Draw above cap
  // throttles token generation and training for the whole player.
  state.players.forEach((p, i) => {
    if (!p.alive) return;
    let cap = 0;
    let draw = 0;
    for (const b of playerBuildings(state, i)) {
      if (b.buildTicks > 0) continue;
      if ((b.disabledTicks ?? 0) > 0) continue; // DDoS'd: off the grid entirely
      const capAdd =
        b.type === 'substation' && FACTIONS[p.faction].substationPowerOverride !== undefined
          ? FACTIONS[p.faction].substationPowerOverride!
          : BUILDINGS[b.type].powerCapAdd;
      cap += capAdd;
      draw += BUILDINGS[b.type].powerDraw;
    }
    p.powerCap = cap;
    p.powerDraw = draw;
    const browned = draw > cap;
    if (browned && !w[i].brownedOut) {
      state.events.push({
        tick: state.tick,
        player: i,
        type: 'brownout',
        message: 'Brownout! Power draw exceeds capacity — token generation and training are throttled. Build a Substation.',
      });
    } else if (!browned && w[i].brownedOut) {
      state.events.push({
        tick: state.tick,
        player: i,
        type: 'brownoutEnded',
        message: 'Power restored: running at full capacity.',
      });
    }
    w[i].brownedOut = browned;
  });

  for (const b of buildings(state)) {
    const p = state.players[b.owner];
    if (!p.alive) continue;
    const bs = BUILDINGS[b.type];
    if (b.buildTicks > 0) continue; // under construction: no burn yet, no output

    // burn — you pay for the building even while it's DDoS'd (it's still yours,
    // just not serving)
    const burn = (bs.burnPerSec * buildingBurnMult(p)) / TPS;
    p.budget -= burn;
    w[b.owner].tickBurn += burn;

    // DDoS: offline for the duration — no token gen, synthetic data, training,
    // or tower fire this tick. Countdown ticks down here.
    if ((b.disabledTicks ?? 0) > 0) {
      b.disabledTicks!--;
      continue;
    }

    // token generation (throttled during brownout)
    if (bs.tokensPerSec > 0) {
      const gen = (bs.tokensPerSec * FACTIONS[p.faction].tokenGenMult * brownoutMult(p)) / TPS;
      p.tokens = Math.min(p.tokenCap, p.tokens + gen);
      p.stats.tokensGenerated += gen;
      w[b.owner].tickTokens += gen;
    }

    // synthetic data production: tokens in, data out (brownout-throttled)
    if (b.type === 'syntheticData') {
      const tokIn = (ECONOMY.syntheticTokensPerSec * brownoutMult(p)) / TPS;
      const dataOut = (ECONOMY.syntheticDataPerSec * brownoutMult(p)) / TPS;
      if (p.tokens >= tokIn && p.data < p.dataCap) {
        p.tokens -= tokIn;
        w[b.owner].tickTokens -= tokIn;
        p.data = Math.min(p.dataCap, p.data + dataOut);
        p.stats.dataHarvested += dataOut;
      }
    }

    // training queue (throttled during brownout)
    if (b.queue.length > 0) {
      b.trainTicks -= brownoutMult(p);
      if (b.trainTicks <= 0) {
        const ut = b.queue.shift()!;
        spawnUnit(state, b, ut);
        if (b.queue.length > 0) {
          b.trainTicks = secToTicks(
            UNITS[b.queue[0]].trainTimeSec * FACTIONS[p.faction].buildSpeedMult,
          );
        }
      }
    }

    // tower attacks
    if (bs.attack) {
      const bAny = b as Building & { _cd?: number };
      bAny._cd = Math.max(0, (bAny._cd ?? 0) - 1);
      if (bAny._cd === 0) {
        let best: Entity | null = null;
        let bestD = Infinity;
        for (const e of state.entities) {
          if (e.kind !== 'unit' || e.owner === b.owner || e.owner === -1) continue;
          const d = dist(buildingCenter(b), e.pos);
          if (d <= bs.attack.range && d < bestD) {
            bestD = d;
            best = e;
          }
        }
        if (best) {
          dealDamage(state, b, best);
          bAny._cd = secToTicks(bs.attack.periodSec);
        }
      }
    }
  }
}

function spawnUnit(state: GameState, from: Building, type: UnitType): void {
  const us = UNITS[type];
  state.players[from.owner].stats.unitsTrained++;
  const c = buildingCenter(from);
  const s = BUILDINGS[from.type].size;
  const pos = { x: c.x + s / 2 + 0.8, y: c.y + s / 2 + 0.8 };
  const u: Unit = {
    id: state.nextEntityId++,
    kind: 'unit',
    type,
    owner: from.owner,
    pos,
    hp: us.hp,
    maxHp: us.hp,
    order: { kind: 'idle' },
    path: [],
    attackCooldown: 0,
    carryingData: 0,
    anomalyTicks: 0,
    serving: false,
  };
  if (us.ttlSec) u.expiresTick = state.tick + secToTicks(us.ttlSec);
  // serve-agents are stamped with the generation they were trained at; the
  // stamp never changes — the frontier moves around them (Economy 3.0)
  if (us.serves) u.gen = state.players[from.owner].modelGen;
  state.entities.push(u);
  if (from.rallyPoint) {
    u.order = { kind: 'move', target: from.rallyPoint };
    u.path = findPath(state, u.pos, from.rallyPoint);
  }
}

function tickPlayers(state: GameState, rng: Rng): void {
  const w = work(state);
  const graceTicks = secToTicks(ECONOMY.insolvencyGraceSec);

  state.players.forEach((p, i) => {
    if (!p.alive) return;

    // cyber-ops timers: attacker cooldowns and the victim-side poison penalty
    if (p.cyber) {
      if (p.cyber.injectCd > 0) p.cyber.injectCd--;
      if (p.cyber.ddosCd > 0) p.cyber.ddosCd--;
      if (p.cyber.poisonCd > 0) p.cyber.poisonCd--;
      if (p.cyber.poisonedTicks > 0) p.cyber.poisonedTicks--;
    }

    // unit burn
    for (const u of playerUnits(state, i)) {
      const burn = (UNITS[u.type].burnPerSec * FACTIONS[p.faction].unitBurnMult) / TPS;
      p.budget -= burn;
      w[i].tickBurn += burn;
      if (u.anomalyTicks > 0) {
        const extra = burn * 2;
        p.budget -= extra;
        w[i].tickBurn += extra;
      }
    }

    // recompute token & data caps from operational buildings + base
    let cap = ECONOMY.baseTokenCap;
    let dCap = ECONOMY.dataCapBase;
    for (const b of playerBuildings(state, i)) {
      if (b.buildTicks === 0) {
        cap += BUILDINGS[b.type].tokenCapAdd;
        dCap += BUILDINGS[b.type].dataCapAdd;
      }
    }
    p.tokenCap = cap;
    p.tokens = Math.min(p.tokens, cap);
    p.dataCap = dCap;
    p.data = Math.min(p.data, dCap);

    // launch buzz countdown (temporary premium for pushing the frontier)
    if (p.buzzTicks > 0) p.buzzTicks--;

    // frontier training run progress (throttled during brownout; cancelled
    // if the training cluster is destroyed — the investment is lost)
    const tr = w[i].trainingRun;
    if (tr) {
      const cluster = getEntity(state, tr.buildingId);
      if (!cluster || cluster.kind !== 'building' || cluster.hp <= 0) {
        w[i].trainingRun = null;
      } else {
        tr.ticksLeft -= brownoutMult(p);
        if (tr.ticksLeft <= 0) {
          w[i].trainingRun = null;
          p.modelGen++;
          // fine-tunes don't transfer to a new base model (RAG does)
          const ftReset = p.doctrine === 'finetune' && p.fineTuneJobs > 0;
          if (ftReset) p.fineTuneJobs = 0;
          const ftNote = ftReset ? ' Fine-tune bonuses reset — they don’t transfer to the new base model.' : '';
          if (p.modelGen > state.frontierGen) {
            state.frontierGen = p.modelGen;
            p.buzzTicks = secToTicks(FRONTIER.launchBuzzSec);
            state.events.push({
              tick: state.tick,
              player: i,
              type: 'frontierAdvanced',
              message: `${p.name} shipped Model Gen ${p.modelGen} — the frontier moved! Older models reprice down; premium segments demand Gen ${Math.max(0, state.frontierGen - 1)}+.${ftNote}`,
            });
          } else {
            state.events.push({
              tick: state.tick,
              player: i,
              type: 'modelShipped',
              message: `${p.name} shipped Model Gen ${p.modelGen} (fast-follow) — new agents serve at current pricing.${ftNote}`,
            });
          }
        }
      }
    }

    // fine-tuning job progress (throttled during brownout; cancelled if the
    // training cluster is destroyed — the investment is lost)
    const ft = w[i].fineTune;
    if (ft) {
      const cluster = getEntity(state, ft.buildingId);
      if (!cluster || cluster.kind !== 'building' || cluster.hp <= 0) {
        w[i].fineTune = null;
      } else {
        ft.ticksLeft -= brownoutMult(p);
        if (ft.ticksLeft <= 0) {
          w[i].fineTune = null;
          p.fineTuneJobs++;
          state.events.push({
            tick: state.tick,
            player: i,
            type: 'fineTuneComplete',
            message: `Fine-tuning complete: permanent +${Math.round(ECONOMY.fineTuneRevenueBonus * 100)}% revenue (${p.fineTuneJobs}/${ECONOMY.fineTuneMaxJobs}).`,
          });
        }
      }
    }

    // research progress (parallel tracks, one per lab type)
    const rw = w[i];
    if (rw.research.length > 0) {
      for (const job of rw.research) job.ticksLeft--;
      const done = rw.research.filter((r) => r.ticksLeft <= 0);
      for (const job of done) {
        p.techs.push(job.tech);
        state.events.push({
          tick: state.tick,
          player: i,
          type: 'techResearched',
          message: `${TECHS[job.tech].name} researched.`,
        });
      }
      rw.research = rw.research.filter((r) => r.ticksLeft > 0);
    }
    if (rw.phaseWork) {
      rw.phaseWork.ticksLeft--;
      if (rw.phaseWork.ticksLeft <= 0) {
        p.phase = Math.min(2, p.phase + 1) as Phase;
        state.events.push({
          tick: state.tick,
          player: i,
          type: 'phaseAdvanced',
          message: `${p.name} reached the ${['Crawl', 'Walk', 'Run'][p.phase]} phase!`,
        });
        rw.phaseWork = null;
      }
    }

    // anomaly auto-remediation via FinOps center
    const hasCenter = playerBuildings(state, i).some(
      (b) => b.type === 'finopsCenter' && b.buildTicks === 0,
    );
    if (hasCenter) {
      const clearAfter = hasTech(p, 'anomalyDetection')
        ? secToTicks(3)
        : secToTicks(8);
      const total = secToTicks(ECONOMY.anomalyDurationSec);
      for (const u of playerUnits(state, i)) {
        if (u.anomalyTicks > 0 && total - u.anomalyTicks >= clearAfter) {
          u.anomalyTicks = 0;
          state.events.push({
            tick: state.tick,
            player: i,
            type: 'anomalyCleared',
            message: 'Anomaly auto-remediated by FinOps Command Center.',
            entityId: u.id,
          });
        }
      }
    }

    // insolvency — the runway clock. Trajectory matters: positive net cash
    // flow REWINDS the countdown while you're under $0 (investors will
    // tolerate red ink that's visibly improving), negative net advances it.
    if (p.budget <= 0) {
      const recovering = p.revenuePerSec > p.burnPerSec;
      if (p.insolvencyTicks === 0 && !recovering) {
        state.events.push({
          tick: state.tick,
          player: i,
          type: 'insolvencyWarning',
          message: `${p.name} is out of runway! ${ECONOMY.insolvencyGraceSec}s to get back above $0 — positive cash flow rewinds the clock.`,
        });
      }
      if (recovering) {
        p.insolvencyTicks = Math.max(0, p.insolvencyTicks - 1);
      } else {
        p.insolvencyTicks++;
        if (p.insolvencyTicks > graceTicks) {
          eliminate(state, i, 'bankrupt');
        }
      }
    } else {
      p.insolvencyTicks = 0;
    }

    // telemetry smoothing (per-second rates)
    p.stats.revenueEarned += rw.tickRevenue;
    const alpha = 0.08;
    p.revenuePerSec = p.revenuePerSec * (1 - alpha) + rw.tickRevenue * TPS * alpha;
    p.burnPerSec = p.burnPerSec * (1 - alpha) + rw.tickBurn * TPS * alpha;
    p.tokensPerSec = p.tokensPerSec * (1 - alpha) + rw.tickTokens * TPS * alpha;
    rw.tickRevenue = 0;
    rw.tickBurn = 0;
    rw.tickTokens = 0;
  });

  // anomaly rolls
  const period = secToTicks(ECONOMY.anomalyPeriodSec);
  if (state.tick > 0 && state.tick % period === 0) {
    state.players.forEach((p, i) => {
      if (!p.alive) return;
      if (rng.next() >= ECONOMY.anomalyChance) return;
      const agents = playerUnits(state, i).filter(
        (u) => isServeUnit(u.type) && u.anomalyTicks === 0,
      );
      if (agents.length === 0) return;
      const victim = agents[rng.int(0, agents.length - 1)];
      victim.anomalyTicks = secToTicks(ECONOMY.anomalyDurationSec);
      state.events.push({
        tick: state.tick,
        player: i,
        type: 'anomalyStarted',
        message:
          'Cost anomaly: an agent is stuck in a runaway loop, burning tokens and earning nothing!',
        entityId: victim.id,
      });
    });
  }
}

/** Find a clear neutral-entity tile via bounded rejection sampling:
 *  mid-map-ish, unblocked, away from HQs and other markets. Deterministic. */
function findNeutralSpot(state: GameState, rng: Rng, minHqDist: number, minMarketDist: number): Vec2 | null {
  const { width, height } = state.map;
  const hqs = buildings(state).filter((b) => b.type === 'hq');
  const markets = state.entities.filter((e) => e.kind === 'market');
  for (let tries = 0; tries < 40; tries++) {
    const x = rng.int(7, width - 8) + 0.5;
    const y = rng.int(7, height - 8) + 0.5;
    if (isBlocked(state, x, y)) continue;
    if (hqs.some((h) => dist(entityCenter(h), { x, y }) < minHqDist)) continue;
    if (minMarketDist > 0 && markets.some((m) => dist(m.pos, { x, y }) < minMarketDist)) continue;
    return { x, y };
  }
  return null;
}

/** Neutral-world dynamics: markets migrate (real markets move — camping a
 *  segment with towers is a wasting asset) and fresh data periodically
 *  appears once the map runs dry (public datasets keep dropping). */
function tickWorld(state: GameState, rng: Rng): void {
  // market migration
  for (const e of state.entities) {
    if (e.kind !== 'market') continue;
    const m = e as Market;
    if (m.nextMoveTick === undefined || state.tick < m.nextMoveTick) continue;
    const spot = findNeutralSpot(state, rng, 10, 6);
    m.nextMoveTick =
      state.tick + rng.int(ECONOMY.marketMoveMinSec, ECONOMY.marketMoveMaxSec) * TPS;
    if (!spot) continue; // cramped map this cycle; try again next window
    m.pos = spot;
    // serving agents don't get a forwarding address: the segment is gone
    // until someone SEES where it went (fog-honest markets)
    for (const u of units(state)) {
      if (u.order.kind === 'serve' && u.order.targetId === m.id) {
        u.order = { kind: 'idle' };
        u.serving = false;
        u.path = [];
        u.redeploy = true; // they remember what they were doing
      }
    }
    state.events.push({
      tick: state.tick,
      player: -1,
      type: 'marketMoved',
      message: 'Market demand shifted — stranded agents will redeploy once you discover a segment!',
      entityId: m.id,
    });
  }

  // data respawn once the map is nearly mined out
  if (state.tick > 0 && state.tick % secToTicks(ECONOMY.dataRespawnCheckSec) === 0) {
    const total = state.entities
      .filter((e): e is DataNode => e.kind === 'dataNode')
      .reduce((sum, n) => sum + n.remaining, 0);
    if (total < ECONOMY.dataRespawnThreshold) {
      const spot = findNeutralSpot(state, rng, 8, 0);
      if (spot) {
        state.entities.push({
          id: state.nextEntityId++,
          kind: 'dataNode',
          owner: -1,
          pos: spot,
          hp: 1,
          maxHp: 1,
          remaining: ECONOMY.dataRespawnAmount,
        });
        state.events.push({
          tick: state.tick,
          player: -1,
          type: 'dataDiscovered',
          message: 'A fresh public dataset dropped — new data node on the map!',
        });
      }
    }
  }
}

function checkVictory(state: GameState): void {
  if (state.winner !== null) return;
  const alive = state.players
    .map((p, i) => ({ p, i }))
    .filter(({ p }) => p.alive);
  if (state.players.length > 1 && alive.length <= 1) {
    state.winner = alive.length === 1 ? alive[0].i : -1;
    state.events.push({
      tick: state.tick,
      player: state.winner,
      type: 'gameOver',
      message:
        state.winner >= 0
          ? `${state.players[state.winner].name} wins!`
          : 'Mutual destruction. The auditors win.',
    });
  }
}

// ---------------------------------------------------------------------------
// Public tick entry point
// ---------------------------------------------------------------------------

import { botCommands } from './bot';

export function applyTick(state: GameState, commands: IssuedCommand[]): void {
  state.events = [];
  if (state.winner !== null) {
    state.tick++;
    return;
  }
  const rng = new Rng(0);
  rng.setState(state.rngState);

  // Fog-of-war bookkeeping (per-second granularity), refreshed BEFORE bots
  // think so their information is current-but-honest.
  if (state.tick % TPS === 0) updateExplored(state);

  // Bots think deterministically inside the sim; cadence scales with
  // difficulty (easy ponders, hard reacts).
  state.players.forEach((p, i) => {
    if (!p.isBot || !p.alive) return;
    const interval =
      p.difficulty === 'easy' ? 3 * TPS : p.difficulty === 'hard' ? TPS / 2 : TPS;
    if (state.tick % Math.max(1, Math.round(interval)) === 0) {
      for (const cmd of botCommands(state, i)) {
        commands = commands.concat([{ player: i, cmd }]);
      }
    }
  });

  for (const ic of commands) {
    applyCommand(state, ic.player, ic.cmd);
  }

  tickBuildings(state);
  tickUnits(state);
  tickPlayers(state, rng);
  tickWorld(state, rng);
  checkVictory(state);

  state.rngState = rng.getState();
  state.tick++;
  harvestTimelineEvents(state);
  // Sample end-game graph telemetry every interval, plus once on the victory
  // tick so the graphs run all the way to the end (applyTick early-returns on
  // every later call, so the victory tick reaches here exactly once).
  if (state.tick % (TIMELINE_INTERVAL_SEC * TPS) === 0 || state.winner !== null) {
    sampleTimeline(state);
  }
}

/** Deep-clone-safe snapshot (JSON round-trip drops transient _fields, which
 * is fine for saves/late-join but NOT for mid-game determinism — peers must
 * join via the command log or at lobby time). */
export function snapshot(state: GameState): string {
  return JSON.stringify(state);
}

export function restore(json: string): GameState {
  return JSON.parse(json) as GameState;
}
